import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { categories, collections, faqs } from "@/mocks/meta";
import SectionHead from "@/components/feature/SectionHead";
import { ArticleCard, ArticleRow } from "@/components/feature/ArticleCards";

const pick = (slugs: string[]) => slugs.map((s) => articles.find((a) => a.slug === s)).filter(Boolean) as typeof articles;

const HOT_FEATURED = "hotsearch-timeline";
const HOT_LIST = ["fangyan-72h", "datou-pool", "daibohuofan", "xuanxiu-flow"];

export default function Home() {
  useSeo(
    "吃瓜精选｜专题合集与分类盘点，一次看透一个瓜的完整脉络",
    "吃瓜精选为你整理娱乐专题合集与分类盘点：从明星恋情、行业秘闻到综艺名场面、社会热议，按主题汇总系列整理，脉络完整、分类清晰，让你不用拼凑碎片信息，点开即得系统化追瓜内容。"
  );

  const featured = collections.find((c) => c.slug === "year-timeline")!;
  const restCollections = collections.filter((c) => c.slug !== "year-timeline");
  const [activeCat, setActiveCat] = useState(categories[0].slug);
  const filterArticles = useMemo(() => articles.filter((a) => a.category === activeCat).slice(0, 6), [activeCat]);
  const featuredArticle = pick([HOT_FEATURED])[0];
  const hotList = pick(HOT_LIST);

  return (
    <div className="pt-16 md:pt-[72px]">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-background-200/70 bg-background-100/50">
        <div className="pointer-events-none absolute -right-10 top-0 select-none text-[15rem] font-black leading-none text-primary-500/[0.06] md:text-[22rem]">
          瓜
        </div>
        <div className="wrap relative grid grid-cols-1 items-center gap-10 py-12 md:py-16 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-2">
              {["明星恋情", "行业秘闻", "综艺名场面", "社会热议"].map((t) => (
                <span key={t} className="rounded-full border border-primary-200 bg-primary-100/60 px-3 py-1 text-xs font-medium text-primary-800">
                  {t}
                </span>
              ))}
            </div>
            <h1 className="serif mt-6 text-[1.7rem] font-black leading-tight tracking-tight text-foreground-950 md:text-[2.6rem] md:leading-[1.18]">
              把七零八落的「瓜」
              <br />
              拼成<span className="text-primary-600">一个完整故事</span>
            </h1>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-foreground-700 md:text-base">
              从明星恋情到行业秘闻，从综艺名场面到社会热议——我们不做一惊一乍的快讯，只做按主题汇总、系列整理的<span className="font-semibold text-foreground-950">专题合集与分类盘点</span>，让你点开即得一条清晰的完整脉络。
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/#collections"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-fire-line text-base" /> 进入年度专题
              </Link>
              <Link
                to={`/category/${categories[0].slug}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-foreground-300 bg-background-50/70 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                浏览栏目盘点 <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <dl className="mt-9 grid max-w-md grid-cols-3 gap-6 border-t border-background-200 pt-6">
              {[
                { v: String(collections.length), l: "年度专题" },
                { v: String(categories.length), l: "常设栏目" },
                { v: String(articles.length), l: "原创盘点" },
              ].map((s) => (
                <div key={s.l}>
                  <dt className="display text-3xl font-bold text-primary-600">{s.v}</dt>
                  <dd className="mt-1 text-xs text-secondary-600">{s.l}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-5">
            <Link to="/#collections" className="group relative block overflow-hidden rounded-2xl border border-background-200/70">
              <div className="aspect-[4/3] w-full overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=editorial%20magazine%20photography%20of%20a%20warm%20collage%20of%20celebrity%20gossip%20headlines%20and%20red%20carpet%20moments%2C%20crimson%20and%20antique%20gold%20palette%2C%20glossy%20print%20texture%2C%20dramatic%20directional%20lighting%2C%20premium%20entertainment%20magazine%20cover%20aesthetic%2C%20cinematic%20film%20grain&width=1200&height=900&seq=hero-picks&orientation=portrait"
                  alt="吃瓜精选专题合集封面视觉"
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.05]"
                />
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground-950/80 to-transparent p-5 pt-14">
                <p className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
                  <i className="ri-bookmark-fill" /> 主编推荐 · 专题合集
                </p>
                <p className="serif mt-2 text-lg font-bold leading-snug text-background-50">{featured.title}</p>
                <p className="mt-1 text-xs text-background-300">{featured.subtitle}</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ 四大栏目 ============ */}
      <section id="categories" className="wrap scroll-mt-24 py-12 md:py-16" aria-label="栏目盘点入口">
        <div className="flex items-end justify-between gap-4">
          <SectionHead eyebrow="栏目盘点" title="四大常设栏目，各归其位" desc="内容按主题归类，想找哪个方向都能快速定位。" />
          <Link to={`/category/${categories[0].slug}`} className="hidden shrink-0 items-center gap-1 text-sm font-semibold text-primary-700 hover:text-primary-800 md:inline-flex">
            全部盘点 <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-100/40 transition-colors hover:border-primary-300/70"
            >
              <div className="relative aspect-[16/10] w-full overflow-hidden bg-background-200">
                <img
                  src={c.cover}
                  alt={`${c.name}分类盘点封面`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.05]"
                />
                <span className="absolute right-3 top-3 rounded-full bg-background-50/90 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-secondary-700 backdrop-blur">
                  {c.nameEn}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h3 className="serif text-lg font-bold text-foreground-950">{c.name}</h3>
                <p className="mt-1.5 line-clamp-2 text-sm text-foreground-600">{c.brief}</p>
                <p className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary-700 transition-colors group-hover:gap-2">
                  进入栏目 <i className="ri-arrow-right-line" />
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ============ 年度专题 ============ */}
      <section id="collections" className="scroll-mt-24 bg-background-100/60 py-12 md:py-16">
        <div className="wrap">
          <SectionHead eyebrow="专题合集" title="按主题汇总的完整脉络" desc="一个瓜不再是一张糊图、一句爆料，而是一条可以从头读到尾的时间线。" />
        </div>
        <div className="wrap mt-8 grid grid-cols-1 gap-4 md:gap-5">
          {/* featured wide */}
          <Link to={`/collection/${featured.slug}`} className="group relative block overflow-hidden rounded-2xl">
            <div className="aspect-[16/9] w-full overflow-hidden bg-foreground-900 md:aspect-[21/9]">
              <img
                src={featured.cover}
                alt={featured.title}
                className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.04]"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/55 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-end p-5 md:max-w-[60%] md:p-9">
              <p className="inline-flex w-fit items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
                <i className="ri-flashlight-fill" /> 本期主打
              </p>
              <h3 className="serif mt-3 text-2xl font-black leading-tight text-background-50 md:text-4xl">{featured.title}</h3>
              <p className="mt-2 hidden max-w-xl text-sm leading-relaxed text-background-200 md:block">{featured.intro}</p>
              <p className="mt-3 text-sm text-accent-200">{featured.meta}</p>
              <span className="mt-5 inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-900 transition-colors group-hover:bg-accent-400">
                开始阅读完整脉络 <i className="ri-arrow-right-line" />
              </span>
            </div>
          </Link>

          {/* two more */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5">
            {restCollections.map((c) => (
              <Link
                key={c.slug}
                to={`/collection/${c.slug}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 transition-colors hover:border-accent-300/70"
              >
                <div className="relative aspect-[16/8] w-full overflow-hidden bg-background-200">
                  <img src={c.cover} alt={c.title} className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.05]" />
                  <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-2.5 py-1 text-xs font-semibold text-primary-700 backdrop-blur">{c.meta}</span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="serif text-lg font-bold leading-snug text-foreground-950">{c.title}</h3>
                  <p className="mt-1 text-sm text-secondary-600">{c.subtitle}</p>
                  <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-600">{c.intro}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 热搜时间线（热议盘点） ============ */}
      <section className="wrap py-12 md:py-16" aria-label="热议盘点">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <SectionHead eyebrow="今日热议" title="一场热搜的完整时间线" />
            {featuredArticle && (
              <Link to={`/article/${featuredArticle.slug}`} className="group mt-6 block overflow-hidden rounded-2xl border border-background-200/70 bg-background-100/40">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-background-200 md:aspect-auto md:h-full">
                    <img src={featuredArticle.cover} alt={featuredArticle.title} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]" />
                  </div>
                  <div className="flex flex-col justify-center p-6 md:p-8">
                    <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-700">
                      <i className="ri-hotspot-fill" /> 全网热读
                    </span>
                    <h3 className="serif mt-3 text-xl font-bold leading-snug text-foreground-950 md:text-2xl">{featuredArticle.title}</h3>
                    <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-600">{featuredArticle.excerpt}</p>
                    <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-700">
                      展开时间线复盘 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                    </p>
                  </div>
                </div>
              </Link>
            )}
          </div>

          <div className="lg:col-span-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-foreground-950">热门盘点</h3>
              <span className="text-xs text-secondary-500">按热度排序</span>
            </div>
            <div className="mt-4 space-y-3">
              {hotList.map((a, i) => (
                <ArticleRow key={a.slug} a={a} index={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ 栏目精选（可切换） ============ */}
      <section className="bg-background-100/60 py-12 md:py-16" aria-label="栏目精选">
        <div className="wrap">
          <SectionHead eyebrow="栏目精选" title="今天，想从哪个栏目开始？" desc="点选栏目即可切换该方向下的最新盘点，一条脉络都不错过。" />
          <div className="mt-7 inline-flex max-w-full flex-wrap gap-2 rounded-full border border-background-200 bg-background-50 p-1">
            {categories.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => setActiveCat(c.slug)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  activeCat === c.slug ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-700"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
            {filterArticles.map((a) => (
              <ArticleCard key={a.slug} a={a} />
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to={`/category/${activeCat}`}
              className="inline-flex items-center gap-2 rounded-full border border-primary-300 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-500 hover:text-background-50"
            >
              查看「{categories.find((c) => c.slug === activeCat)?.name}」全部盘点 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ 方法论 ============ */}
      <section className="wrap py-12 md:py-16" aria-label="吃瓜方法论">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="我们的方法" title="你省去拼凑，我们来整理" />
            <blockquote className="mt-6 border-l-4 border-primary-500 pl-4">
              <p className="serif text-lg font-semibold leading-relaxed text-foreground-800">
                「吃瓜最累的不是看热闹，而是从无数碎片里自己拼出一件事的来龙去脉。我们的工作，就是把这一步替你做完。」
              </p>
            </blockquote>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-foreground-600">
              每一条脉络，我们都坚持「多信源、拉时间线、按章节呈现」：同框图放在时间轴上，多方回应放在同一视角下，反转与新料随时补入，让追瓜真正变得省心。
            </p>
            <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800">
              了解我们是谁 <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                { n: "01", t: "采集信源", d: "收敛同框、发声、澄清等原始信息，剔除噪声与断章。" },
                { n: "02", t: "拉时间线", d: "按时间与因果串起关键节点，还原事件推进的真实顺序。" },
                { n: "03", t: "编成专题", d: "用栏目与专题承载脉络，章节分明，一次读完一段故事。" },
                { n: "04", t: "持续更新", d: "新料出现即补入对应脉络，让内容不断逼近完整。" },
              ].map((s) => (
                <div key={s.n} className="rounded-xl border border-background-200/70 bg-background-100/50 p-5">
                  <span className="display text-2xl font-bold text-accent-500">{s.n}</span>
                  <h3 className="serif mt-2 text-lg font-bold text-foreground-950">{s.t}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ 预览 ============ */}
      <section className="wrap py-12 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead eyebrow="常见问题" title="关于吃瓜精选，你可能想问" />
            <Link to="/faq" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600">
              查看全部问答 <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="lg:col-span-8">
            <div className="divide-y divide-background-200 rounded-xl border border-background-200/70 bg-background-100/40">
              {faqs.slice(0, 3).map((f) => (
                <details key={f.q} className="group px-5 py-4 md:px-6">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold text-foreground-900">
                    {f.q}
                    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-background-200/80 text-foreground-600 transition-transform group-open:rotate-45">
                      <i className="ri-add-line" />
                    </span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}